import * as React from "react";
import { useCallback } from "react";
import { Formik, Form, Field } from "formik";
import mapValues from "lodash/mapValues";
import { useTranslation } from "react-i18next";
import { validateExternalIdRequiresArn } from "integrations/aws/utils/awsValidation";
import ArchiveBackendNameDescriptionFormGroup from "archive/components/ArchiveBackendNameDescriptionFormGroup";
import ArchiveBackendOutputPathFieldGroup from "archive/components/ArchiveBackendOutputPathFieldGroup";
import { FILESYSTEM_TYPE, S3_TYPE, GCS_TYPE, ABS_TYPE } from "archive/ArchiveStore";
import {
  S3_COMPATIBLE_OPTIONS,
  getS3Variant,
  isS3CompatibleType,
  resolveS3CompatibleType
} from "archive/logic/s3CompatibleProviders";
import { FormSubmit, Select } from "components/common";
import { HelpBlock, Input } from "components/bootstrap";
import useHistory from "routing/useHistory";
import ArchiveBackendGCSFieldGroup from "archive/components/ArchiveBackendGCSFieldGroup";
import GCSSetupInfo from "components/gcs/GCSSetupInfo";
import ArchiveBackendS3FieldGroup from "archive/components/ArchiveBackendS3FieldGroup";
import AWSRegionsFormGroup from "archive/components/aws/AWSRegionsFormGroup";
import ArchiveBackendABSFieldGroup from "archive/components/ArchiveBackendABSFieldGroup";
import ArchiveBackendFSFieldGroup from "archive/components/ArchiveBackendFSFieldGroup";
const defaultFormValues = { title: "", description: "", settings: { type: void 0 } };
const BACKEND_TYPE_OPTIONS = [
  { value: S3_TYPE, label: "S3" },
  ...S3_COMPATIBLE_OPTIONS,
  { value: GCS_TYPE, label: "GCS" },
  { value: ABS_TYPE, label: "Azure Blob" }
];
const _formatBackendValidationErrors = (backendErrors) => {
  const backendErrorStrings = mapValues(backendErrors, (errorArray) => `${errorArray.join(" ")}`);
  const { title, description, ...rest } = backendErrorStrings;
  return {
    title: title || void 0,
    description: description || void 0,
    settings: {
      ...rest
    }
  };
};
const validateArchiveBackendForm = (values) => {
  var _a;
  const errors = {};
  if (((_a = values.settings) == null ? void 0 : _a.type) === S3_TYPE) {
    const s3Settings = values.settings;
    const externalIdError = validateExternalIdRequiresArn(s3Settings == null ? void 0 : s3Settings.aws_assume_role_arn, s3Settings == null ? void 0 : s3Settings.aws_external_id);
    if (externalIdError) {
      errors.settings = {
        ...errors.settings,
        aws_external_id: externalIdError
      };
    }
  }
  return errors;
};
const BackendSpecificFields = ({ type }) => {
  if (isS3CompatibleType(type)) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ArchiveBackendS3FieldGroup, { variant: getS3Variant(type) }), /* @__PURE__ */ React.createElement(AWSRegionsFormGroup, { variant: getS3Variant(type) }));
  }
  switch (type) {
    case GCS_TYPE:
      return /* @__PURE__ */ React.createElement(ArchiveBackendGCSFieldGroup, null);
    case ABS_TYPE:
      return /* @__PURE__ */ React.createElement(ArchiveBackendABSFieldGroup, null);
    case FILESYSTEM_TYPE:
      return /* @__PURE__ */ React.createElement(ArchiveBackendFSFieldGroup, null);
    default:
      return null;
  }
};
const ArchiveBackendConfigForm = ({
  backend = {
    id: void 0,
    title: "",
    description: "",
    settings: {
      type: void 0
    }
  },
  submitButtonText,
  submitLoadingText,
  checkOutputPath,
  updateConfiguration,
  cancelURL
}) => {
  let initialValues;
  const { t } = useTranslation("enterprise_archive");
  const history = useHistory();
  const backendTypeOptions = [
    { value: FILESYSTEM_TYPE, label: t("archive_backend_config_form.file_system") },
    ...BACKEND_TYPE_OPTIONS
  ];
  if (backend == null ? void 0 : backend.id) {
    const { id, title, description, settings } = backend;
    initialValues = { id, title, description, settings };
  } else {
    initialValues = defaultFormValues;
  }
  const _handleSubmit = (values, { setErrors }) => {
    const resolvedValues = {
      ...values,
      settings: { ...values.settings, type: resolveS3CompatibleType(values.settings.type) }
    };
    return updateConfiguration(resolvedValues).catch((error) => {
      var _a, _b;
      if (typeof ((_b = (_a = error == null ? void 0 : error.additional) == null ? void 0 : _a.body) == null ? void 0 : _b.errors) === "object") {
        setErrors(_formatBackendValidationErrors(error.additional.body.errors));
      }
    });
  };
  const onCancel = () => history.push(cancelURL);
  const resetBackendSpecificFields = useCallback((setFieldValue) => {
    const allBackendFields = {
      bucket: void 0,
      endpoint_uri: void 0,
      aws_access_key_id: void 0,
      aws_secret_access_key: void 0,
      aws_assume_role_arn: void 0,
      aws_external_id: void 0,
      aws_region: void 0,
      service_account: void 0,
      container: void 0,
      account_name: void 0,
      key: void 0,
      spool_directory: void 0,
      high_watermark: void 0
    };
    Object.entries(allBackendFields).forEach(([field, value]) => {
      setFieldValue(`settings.${field}`, value);
    });
  }, []);
  return /* @__PURE__ */ React.createElement(Formik, { initialValues, onSubmit: _handleSubmit, validate: validateArchiveBackendForm }, ({ values, setFieldValue, isSubmitting }) => {
    const resetFields = () => resetBackendSpecificFields(setFieldValue);
    return /* @__PURE__ */ React.createElement(Form, null, values.settings.type === GCS_TYPE && /* @__PURE__ */ React.createElement(GCSSetupInfo, null), /* @__PURE__ */ React.createElement(Field, { name: "settings.type" }, () => /* @__PURE__ */ React.createElement(
      Input,
      {
        help: t("archive_backend_config_form.backend_type_help"),
        id: "default-backend-type-select",
        label: t("archive_backend_config_form.backend_type_label")
      },
      /* @__PURE__ */ React.createElement(
        Select,
        {
          id: "backendType",
          name: "BackendType",
          placeholder: t("archive_backend_config_form.backend_type_placeholder"),
          options: backendTypeOptions,
          disabled: !!values.id,
          onChange: (option) => {
            resetFields();
            setFieldValue("settings.type", option);
          },
          value: values.settings.type
        }
      ),
      /* @__PURE__ */ React.createElement(HelpBlock, null)
    )), values.settings.type && /* @__PURE__ */ React.createElement(ArchiveBackendNameDescriptionFormGroup, null), values.settings.type && /* @__PURE__ */ React.createElement(BackendSpecificFields, { type: values.settings.type }), values.settings.type && /* @__PURE__ */ React.createElement(ArchiveBackendOutputPathFieldGroup, { checkOutputPath }), values.settings.type && /* @__PURE__ */ React.createElement(
      FormSubmit,
      {
        submitButtonText,
        submitLoadingText,
        isSubmitting,
        isAsyncSubmit: true,
        onCancel
      }
    ));
  });
};
export default ArchiveBackendConfigForm;
